// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Third party imports
import notify from 'devextreme/ui/notify';
import { DxDataGridComponent, DxFormComponent } from 'devextreme-angular';

// Constant imports
import { Constants } from '../../../../_constants/constants';
import { StringFormatConstants } from '../../../../_constants/string-format-constants';
import { MessageInterval } from '../../../../_constants/messages-intervals';

// Service imports
import { ServerErrorService } from '../../../../_services/shared-services/server-error.service';
import { ARCreditApplicationService } from '../../_services/account-receivable/ar-credit-application.service';
import { PrecisionService } from '../../../../_services/shared-services/precision.service';

// Model imports
import { ARManualCreditApplicationParameterDto } from '../../_models/account-receivable/credit-applications/ar-manual-credit-application-parameter-dto';
import { ARCreditApplicationEditorResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-application-editor-result-dto';
import { ARCreditApplicationTargetTransactionResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-application-target-transaction-result-dto';
import { ARPostCreditApplicationParametersDto } from '../../_models/account-receivable/credit-applications/ar-post-credit-application-parameters-dto';
import { ARCreditApplicationTargetParametersDto } from '../../_models/account-receivable/credit-applications/ar-credit-application-target-parameters-dto';
import { ARCreditApplicationTransactionItemParametersDto } from '../../_models/account-receivable/credit-applications/ar-credit-application-transaction-item-parameters-dto';
import { ARCreditApplicationTransactionItemResultDto } from '../../_models/account-receivable/credit-applications/ar-credit-application-transaction-item-result-dto';
import { ApplyAmountDataEntry } from '../../_models/account-receivable/credit-applications/apply-amount';

@Component({
    selector: 'app-ar-manual-credit-application',
    templateUrl: './ar-manual-credit-application.component.html',
    styleUrls: ['./ar-manual-credit-application.component.scss']
})
export class ARManualCreditApplicationComponent implements OnInit {
    @ViewChild('targetGrid', { static: false }) targetGrid: DxDataGridComponent;
    @ViewChild('sourceTransactionForm', { static: false }) sourceTransactionForm: DxFormComponent;
    @ViewChild('arMessageBox', { static: false }) arMessageBox: MessageBoxComponent;

    @Input() visible = false;
    @Input() transactionType: string;
    @Input() transactionId: number;
    @Input() communityId: number;
    @Input() managementCompanyId: number;
    @Output() onManualCAEditorClose = new EventEmitter<boolean>();

    loadingVisible = false;
    currencyFormat: any;
    dateFormat = StringFormatConstants.dateFormat;
    defaultLabelLocation: string = Constants.defaultFormLabelLocation;

    popUpTitle: string;
    editorResult: ARCreditApplicationEditorResultDto;
    totalAppliedAmount = 0;
    remainingUnappliedAmount: number;
    tempUnappliedAmount: number;
    minApplyDate: Date;
    applyDate: Date;

    showSpecifyApplyAmount = false;

    creditApplicationSourceLineItems: ApplyAmountDataEntry[] = [];
    selectedTargetRow: ARCreditApplicationTargetTransactionResultDto;

    iconCancel = IconConstants.CANCEL;
    iconCheck = IconConstants.CHECK;

    creditApplicationTargetItems: Array<ARCreditApplicationTransactionItemParametersDto> = []; // final targetItem list to send it to API

    constructor(
        private readonly arCreditApplicationService: ARCreditApplicationService,
        private readonly errorService: ServerErrorService,
        private readonly precisionService: PrecisionService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        if (Utils.notNullOrEmpty(this.transactionId) && Utils.notNullOrEmpty(this.transactionType)) {
            this.popUpTitle = `Manual Credit Application for ${this.transactionType} ${this.transactionId}`;
            this.populateManualCAEditor();
        }
    }

    onHidden(): void {
        this.popUpTitle = '';
        this.closeCreditApplication();
    }

    applyAmountFocus(target: ARCreditApplicationTargetTransactionResultDto): void {
        target.isSelect = true;
        this.selectedTargetRow = target;
        this.prepareLineItemEditor();
        if (Utils.isTrue(target.isSelect)) {
            // If multiple source Items, then show specify applyAmount popup
            if (
                Utils.notNullOrEmpty(this.creditApplicationSourceLineItems) &&
                this.creditApplicationSourceLineItems.length > 1
            ) {
                // If we already applied some amount. Getting the previously target line item applied amount info.
                this.creditApplicationTargetItems.forEach((transactionItem) => {
                    if (Utils.equals(transactionItem['targetId'], target.id)) {
                        this.creditApplicationSourceLineItems.forEach((sourceLineItemCreditApp) => {
                            if (Utils.equals(sourceLineItemCreditApp.itemId, transactionItem['sourceItemId'])) {
                                // itemId is lineItemId
                                sourceLineItemCreditApp.targetLineItemCreditApps = transactionItem['targetItems'];
                            }
                        });
                    }
                });
                this.showSpecifyApplyAmount = true;
            } else {
                // If single source and single target
                if (target.lineItems.length === 1) {
                    if (Utils.isNullOrUndefined(target.appliedAmount)) {
                        if (this.remainingUnappliedAmount > target.openBalance) {
                            target.appliedAmount = target.openBalance;
                            target.lineItems[0].appliedAmount = target.openBalance;
                        } else {
                            target.appliedAmount = this.precisionService.getRoundedNumber(
                                target.openBalance - this.remainingUnappliedAmount
                            );
                            target.lineItems[0].appliedAmount = this.precisionService.getRoundedNumber(
                                target.openBalance - this.remainingUnappliedAmount
                            );
                        }
                        this.calculateRemainingAppliedAmount();
                    }
                } else {
                    // If single source, but multiple targets
                    if (
                        Utils.notEquals(target.appliedAmount, target.openBalance) ||
                        Utils.isFalse(target.isAmountEditable)
                    ) {
                        this.creditApplicationTargetItems.forEach((transactionItem) => {
                            if (Utils.equals(transactionItem['targetId'], target.id)) {
                                this.creditApplicationSourceLineItems.forEach((sourceLineItemCreditApp) => {
                                    if (Utils.equals(sourceLineItemCreditApp.itemId, transactionItem['sourceItemId'])) {
                                        // itemId is lineItemId
                                        sourceLineItemCreditApp.targetLineItemCreditApps =
                                            transactionItem['targetItems'];
                                    }
                                });
                            }
                        });
                        this.showSpecifyApplyAmount = true;
                    }
                }
            }
        }

        if (Utils.isTrue(target.isSelect) && Utils.equals(target.appliedAmount, 0)) {
            // remove target selection (main grid) if no amount is applied
            target.isSelect = false;
        }
    }

    onApplyAmountValueChanged(event: any, target: ARCreditApplicationTargetTransactionResultDto): void {
        let remainingUnappliedAmount = this.remainingUnappliedAmount; // to reset the this.remainingUnappliedAmount
        if (Utils.notEquals(event.value, target.appliedAmount)) {
            remainingUnappliedAmount = this.precisionService.getRoundedNumber(
                this.remainingUnappliedAmount + target.appliedAmount
            );
        }

        if (event.value > remainingUnappliedAmount) {
            notify('The apply amount exceeds the unapply amount.', 'error', MessageInterval.errorMsgInterval);
            event.value = 0;
        } else if (event.value > target.openBalance) {
            notify('The apply amount exceeds the remaining open balance.', 'error', MessageInterval.errorMsgInterval);
            event.value = 0;
        }
        this.selectedTargetRow = target;
        this.prepareLineItemEditor();
        target.isSelect = true;

        // If multiple source Items, then show specify applyAmount popup
        if (
            Utils.notNullOrEmpty(this.creditApplicationSourceLineItems) &&
            this.creditApplicationSourceLineItems.length > 1
        ) {
            this.selectedTargetRow = target;
            // If we already applied some amount. Getting the previously target line item applied amount info.
            this.creditApplicationTargetItems.forEach((transactionItem) => {
                if (Utils.equals(transactionItem['targetId'], target.id)) {
                    this.creditApplicationSourceLineItems.forEach((sourceLineItemCreditApp) => {
                        if (Utils.equals(sourceLineItemCreditApp.itemId, transactionItem['sourceItemId'])) {
                            // itemId is lineItemId
                            sourceLineItemCreditApp.targetLineItemCreditApps = transactionItem['targetItems'];
                        }
                    });
                }
            });
            this.showSpecifyApplyAmount = true;
        } else {
            // If single source Item, and has single/multiple target items
            target.appliedAmount = event.value;
            if (target.appliedAmount <= target.openBalance) {
                if (target.lineItems.length > 1) {
                    this.showSpecifyApplyAmount = true;
                } else {
                    target.lineItems[0].appliedAmount = target.appliedAmount;
                    this.selectedTargetRow = target;
                    this.prepareCreditApplicationTargetItems(target); // prepare credit target item
                }
            }
        }
        this.calculateRemainingAppliedAmount();
    }

    onRowSelect(event: any, target: ARCreditApplicationTargetTransactionResultDto): void {
        this.selectedTargetRow = target;

        if (Utils.isTrue(event.value)) {
            this.prepareLineItemEditor();
            // If multiple source Items, then show specify applyAmount popup
            if (
                Utils.notNullOrEmpty(this.creditApplicationSourceLineItems) &&
                this.creditApplicationSourceLineItems.length > 1
            ) {
                // If we already applied some amount. Getting the previously target line item applied amount info.
                this.creditApplicationTargetItems.forEach((transactionItem) => {
                    if (Utils.equals(transactionItem['targetId'], target.id)) {
                        this.creditApplicationSourceLineItems.forEach((sourceLineItemCreditApp) => {
                            if (Utils.equals(sourceLineItemCreditApp.itemId, transactionItem['sourceItemId'])) {
                                // itemId is lineItemId
                                sourceLineItemCreditApp.targetLineItemCreditApps = transactionItem['targetItems'];
                            }
                        });
                    }
                });
                this.showSpecifyApplyAmount = true;
            } else {
                // if single source and single target
                if (target.lineItems.length === 1) {
                    target.isAmountEditable = true;
                    if (this.remainingUnappliedAmount > target.openBalance) {
                        target.appliedAmount = target.openBalance;
                        target.lineItems[0].appliedAmount = target.appliedAmount;
                    } else {
                        target.appliedAmount = this.remainingUnappliedAmount;
                        target.lineItems[0].appliedAmount = target.appliedAmount;
                        target.isAmountEditable = true;
                    }
                    this.selectedTargetRow = target;
                    this.prepareCreditApplicationTargetItems(target); // prepare credit target item
                } else {
                    // if single source and multiple target
                    if (this.remainingUnappliedAmount > target.openBalance) {
                        target.appliedAmount = target.openBalance;
                        target.isAmountEditable = true;
                    } else {
                        target.appliedAmount = this.remainingUnappliedAmount;
                        target.isAmountEditable = true;
                    }
                    // Note: Showing Specify Apply Amount popup if target lineItems length > 1
                    this.selectedTargetRow = target;
                    this.showSpecifyApplyAmount = true;
                }
            }
            this.calculateRemainingAppliedAmount();
        } else {
            // reset the applied amount if selection is removed
            target.appliedAmount = 0;
            target.isAmountEditable = true;
            this.selectedTargetRow = target;
            const updatedCreditApplicationTargetItems: Array<ARCreditApplicationTransactionItemParametersDto> = [];
            this.creditApplicationTargetItems.forEach((transactionItem) => {
                if (Utils.equals(transactionItem['targetId'], target.id)) {
                    if (Utils.notNullOrEmpty(transactionItem['targetItems'])) {
                        transactionItem['targetItems'].forEach((targetItem) => {
                            this.creditApplicationSourceLineItems.forEach((sourceItem) => {
                                if (Utils.equals(targetItem.sourceItemId, sourceItem.itemId)) {
                                    sourceItem.appliedAmount -= targetItem.appliedAmount;
                                    sourceItem.remainingUnapplied += targetItem.appliedAmount;
                                }
                            });

                            this.editorResult.targets.forEach((tItem) => {
                                if (Utils.equals(tItem.id, transactionItem['targetId'])) {
                                    tItem.appliedAmount = 0;
                                    tItem.isSelect = false;
                                }
                            });
                        });
                    }
                } else {
                    updatedCreditApplicationTargetItems.push(transactionItem);
                }
            });

            this.creditApplicationTargetItems = updatedCreditApplicationTargetItems;
            this.calculateRemainingAppliedAmount();
            this.targetGrid.instance.refresh();
        }
    }

    closeSpecifyApplyAmount(targetItemSaved: boolean): void {
        this.showSpecifyApplyAmount = false;
        if (Utils.isFalse(targetItemSaved)) {
            const appliedAmountList = this.creditApplicationSourceLineItems.map((sourceItem) => {
                return Utils.notNullOrEmpty(sourceItem.targetLineItemCreditApps)
                    ? sourceItem.targetLineItemCreditApps.map((d) => d.appliedAmount).reduce((p, n) => p + n)
                    : 0;
            });
            const totalAppliedAmount = Utils.notNullOrEmpty(appliedAmountList)
                ? appliedAmountList.reduce((p, n) => p + n)
                : 0;

            if (Utils.isTrue(this.selectedTargetRow.isSaved)) {
                this.selectedTargetRow.appliedAmount = this.selectedTargetRow.lastSavedAppliedAmount;
                this.selectedTargetRow.isSelect = true;
                this.selectedTargetRow.isAmountEditable = true;
            } else {
                this.selectedTargetRow.isSelect = totalAppliedAmount > 0;
                this.selectedTargetRow.isAmountEditable = true;
                this.selectedTargetRow.appliedAmount = 0;
            }
            this.calculateRemainingAppliedAmount();
        } else {
            this.selectedTargetRow.isSelect = true;
            this.selectedTargetRow.isSaved = true;
            this.selectedTargetRow.lastSavedAppliedAmount = this.selectedTargetRow.appliedAmount;
        }
    }

    onLineItemSaved(event: any): void {
        const editedSourceItems = event.editedSourceItems;
        let targetAppliedAmount = 0;
        // prepare credit target Items
        if (Utils.notNullOrEmpty(editedSourceItems)) {
            // When we have source line items. eg: Adjustment has sourceLineItems
            editedSourceItems.forEach((sourceItem) => {
                const hasSelectedTargetItems = sourceItem.targetItems.find((d) => Utils.isTrue(d.isSelect));
                if (Utils.notNullOrEmpty(hasSelectedTargetItems)) {
                    const transactionItem: ARCreditApplicationTransactionItemParametersDto =
                        new ARCreditApplicationTransactionItemParametersDto();
                    const foundAtIndex = this.creditApplicationTargetItems.findIndex(
                        (d) =>
                            Utils.equals(sourceItem.itemId, d.sourceItemId) &&
                            Utils.equals(this.selectedTargetRow.id, d['targetId'])
                    );

                    transactionItem.sourceItemId = sourceItem.itemId;
                    transactionItem.targetItemId = sourceItem.targetItemId;
                    transactionItem['sourceItem'] = sourceItem;
                    if (Utils.notNullOrEmpty(sourceItem.targetItems)) {
                        transactionItem['targetItems'] = sourceItem.targetItems.map((a) => ({ ...a }));

                        const appliedAmountList = sourceItem.targetItems.map((d) => d.appliedAmount);
                        transactionItem.applyAmount = Utils.notNullOrEmpty(appliedAmountList)
                            ? appliedAmountList.reduce((p, n) => p + n)
                            : 0;
                    } else {
                        transactionItem['targetItems'] = [];
                        transactionItem.applyAmount = 0;
                    }
                    transactionItem['targetId'] = this.selectedTargetRow.id;
                    targetAppliedAmount += transactionItem.applyAmount;
                    if (foundAtIndex > -1) {
                        this.creditApplicationTargetItems[foundAtIndex]['targetId'] = transactionItem['targetId'];
                        transactionItem['targetItems'].forEach((transactionTargetItem) => {
                            const foundTargetItemAtIndex = this.creditApplicationTargetItems[foundAtIndex][
                                'targetItems'
                            ].findIndex(
                                (d) =>
                                    Utils.equals(d.itemId, transactionTargetItem.itemId) &&
                                    Utils.equals(d.sourceItemId, transactionTargetItem.sourceItemId)
                            );

                            if (foundTargetItemAtIndex > -1) {
                                this.creditApplicationTargetItems[foundAtIndex]['targetItems'][
                                    foundTargetItemAtIndex
                                ].appliedAmount = transactionTargetItem.appliedAmount;
                            } else {
                                this.creditApplicationTargetItems[foundAtIndex]['targetItems'].push(
                                    transactionTargetItem
                                );
                            }
                        });
                    } else {
                        this.creditApplicationTargetItems.push(transactionItem);
                    }
                }
            });
            this.selectedTargetRow.appliedAmount = targetAppliedAmount;
        } else {
            // When we don't have source line items. eg: Payment has no sourceLineItems
            this.selectedTargetRow.lineItems.forEach((lineItem) => {
                if (lineItem.appliedAmount > 0) {
                    targetAppliedAmount += lineItem.appliedAmount;
                    const transactionItem: ARCreditApplicationTransactionItemParametersDto =
                        new ARCreditApplicationTransactionItemParametersDto();
                    transactionItem.applyAmount = lineItem.appliedAmount;
                    transactionItem.sourceItemId = lineItem.sourceItemId;
                    transactionItem.targetItemId = lineItem.itemId;
                    transactionItem['targetId'] = this.selectedTargetRow.id;

                    const foundAtIndex = this.creditApplicationTargetItems.findIndex(
                        (d) =>
                            Utils.equals(lineItem.sourceItemId, d.sourceItemId) &&
                            Utils.equals(this.selectedTargetRow.id, d['targetId']) &&
                            Utils.equals(lineItem.itemId, d.targetItemId)
                    );
                    if (foundAtIndex > -1) {
                        this.creditApplicationTargetItems[foundAtIndex].applyAmount = transactionItem.applyAmount;
                    } else {
                        this.creditApplicationTargetItems.push(transactionItem);
                    }
                }
            });
            this.selectedTargetRow.appliedAmount = targetAppliedAmount;
        }

        this.calculateRemainingAppliedAmount();
    }

    onApplyClick(): void {
        const isValid = this.sourceTransactionForm.instance.validate().isValid;
        if (Utils.isTrue(isValid)) {
            const param: ARPostCreditApplicationParametersDto = new ARPostCreditApplicationParametersDto();
            param.communityId = this.communityId;
            param.managementCompanyId = this.managementCompanyId;
            param.sourceId = this.transactionId;
            param.sourceType = this.transactionType;
            param.applyDate = this.applyDate;
            param.targets = [];

            const targets: Array<ARCreditApplicationTargetParametersDto> = [];
            // finalizing Credit Target Items
            this.creditApplicationTargetItems.forEach((creditTargetItem) => {
                const obj: ARCreditApplicationTargetParametersDto = new ARCreditApplicationTargetParametersDto();
                obj.targetId = creditTargetItem['targetId'];
                obj.items = [];
                if (Utils.notNullOrEmpty(creditTargetItem['targetItems'])) {
                    creditTargetItem['targetItems'].forEach((creditTargetItem) => {
                        if (creditTargetItem.appliedAmount > 0) {
                            obj.items.push({
                                targetItemId: creditTargetItem.itemId,
                                sourceItemId: creditTargetItem.sourceItemId,
                                applyAmount: creditTargetItem.appliedAmount
                            });
                        }
                    });
                } else {
                    obj.items.push({
                        targetItemId: creditTargetItem.targetItemId,
                        sourceItemId: creditTargetItem.sourceItemId,
                        applyAmount: creditTargetItem.applyAmount
                    });
                }
                const foundAtIndex = targets.findIndex((t) => Utils.equals(t.targetId, obj.targetId));
                if (foundAtIndex > -1) {
                    targets[foundAtIndex].items.push(...obj.items);
                } else {
                    targets.push(obj);
                }
            });

            param.targets = targets;

            this.loadingVisible = true;
            this.arCreditApplicationService.applyCreditApplication(param).subscribe(
                () => {
                    notify(
                        'Manual credit application applied successfully',
                        'success',
                        MessageInterval.successMsgInterval
                    );
                    this.loadingVisible = false;
                    this.visible = false;
                    this.onManualCAEditorClose.emit(true);
                },
                (error) => {
                    this.loadingVisible = false;
                    this.errorService.displayError(error);
                }
            );
        } else {
            this.arMessageBox.showInfoMessage('Information', 'Mandatory fields cannot be empty.');
        }
    }

    getSourceDimensionText(): string {
        const dimensionList = [];
        if (Utils.notNullOrEmpty(this.editorResult.source.lineItems)) {
            this.editorResult.source.lineItems.forEach((lineItem) => {
                lineItem.dimensions.forEach((dim) => {
                    if (
                        Utils.notNullAndDefined(dim.dimensionTitle) &&
                        Utils.notNullAndDefined(dim.dimensionOptionTitle)
                    ) {
                        dimensionList.push(`${dim.dimensionTitle} : ${dim.dimensionOptionTitle}`);
                    } else {
                        dimensionList.push(`${dim.dimensionCode} : ${dim.dimensionOptionCode}`);
                    }
                });
            });
        } else if (Utils.notNullOrEmpty(this.editorResult.source.dimensions)) {
            this.editorResult.source.dimensions.forEach((dim) => {
                if (Utils.notNullAndDefined(dim.dimensionTitle) && Utils.notNullAndDefined(dim.dimensionOptionTitle)) {
                    dimensionList.push(`${dim.dimensionTitle} : ${dim.dimensionOptionTitle}`);
                } else {
                    dimensionList.push(`${dim.dimensionCode} : ${dim.dimensionOptionCode}`);
                }
            });
        }
        return Utils.notNullOrEmpty(dimensionList) ? Utils.uniqueList(dimensionList).join('<br>') : null;
    }

    getTargetDimensionText(target: ARCreditApplicationTargetTransactionResultDto): string {
        const dimensionList = [];
        target.lineItems.forEach((lineItem) => {
            lineItem.dimensions.forEach((dim) => {
                if (Utils.notNullAndDefined(dim.dimensionTitle) && Utils.notNullAndDefined(dim.dimensionOptionTitle)) {
                    dimensionList.push(`${dim.dimensionTitle} : ${dim.dimensionOptionTitle}`);
                } else {
                    dimensionList.push(`${dim.dimensionCode} : ${dim.dimensionOptionCode}`);
                }
            });
        });
        const dimensionText = Utils.notNullOrEmpty(dimensionList) ? dimensionList.join('<br>') : null;
        return Utils.equals(target.lineItems.length, 1) ? dimensionText : null;
    }

    private prepareLineItemEditor(): void {
        if (Utils.notNullOrEmpty(this.creditApplicationSourceLineItems)) {
            this.creditApplicationSourceLineItems.forEach((sourceItem) => {
                sourceItem.targetLineItemCreditApps = [];
                this.selectedTargetRow.lineItems.forEach(
                    (targetLineItem: ARCreditApplicationTransactionItemResultDto) => {
                        let lineItemObj: ARCreditApplicationTransactionItemResultDto =
                            new ARCreditApplicationTransactionItemResultDto();
                        lineItemObj = Object.assign(lineItemObj, targetLineItem);
                        lineItemObj.appliedAmount = 0;
                        lineItemObj.isSelect = false;
                        lineItemObj.sourceItemId = sourceItem.itemId;
                        targetLineItem.sourceItemId = sourceItem.itemId;
                        sourceItem.targetLineItemCreditApps.push({ ...lineItemObj, sourceItemId: sourceItem.itemId });
                    }
                );
            });
        } else {
            this.selectedTargetRow.lineItems.forEach((targetLineItem: ARCreditApplicationTransactionItemResultDto) => {
                targetLineItem.sourceItemId = this.editorResult.source.id;
            });
        }
    }

    private populateManualCAEditor(): void {
        const param: ARManualCreditApplicationParameterDto = new ARManualCreditApplicationParameterDto();
        param.managementCompanyId = this.managementCompanyId;
        param.communityId = this.communityId;
        param.transactionId = this.transactionId;
        param.transactionType = this.transactionType;
        this.loadingVisible = true;
        this.arCreditApplicationService.getARManualCreditApplicationEditor(param).subscribe(
            (result: ARCreditApplicationEditorResultDto) => {
                this.editorResult = result;
                this.remainingUnappliedAmount = this.precisionService.getRoundedNumber(
                    this.editorResult.source.unappliedAmount
                );
                this.tempUnappliedAmount = this.editorResult.source.unappliedAmount;
                this.initializeEditor();

                this.calculateMinApplyDate();

                this.calculateRemainingAppliedAmount();
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
                this.closeCreditApplication();
            }
        );
    }

    private initializeEditor(): void {
        this.editorResult.targets.forEach((target) => {
            target.isSelect = false;
            target.isAmountEditable = true;

            target.lineItems.forEach((targetLineItem) => {
                targetLineItem.sourceItemId = null;
                targetLineItem.appliedAmount = 0;
            });
        });

        this.creditApplicationSourceLineItems = [];
        const sourceLineItems: Array<ApplyAmountDataEntry> = [];
        if (Utils.notNullOrEmpty(this.editorResult.source.lineItems)) {
            this.editorResult.source.lineItems.forEach((lineItem) => {
                const obj: ApplyAmountDataEntry = new ApplyAmountDataEntry();
                obj.targetLineItemCreditApps = [];
                obj.sourceLineItemCreditApp = new ARCreditApplicationTransactionItemResultDto();
                obj.sourceLineItemCreditApp = Object.assign(obj.sourceLineItemCreditApp, lineItem);
                obj.appliedAmount = Math.abs(obj.appliedAmount);

                obj.itemId = lineItem.itemId;
                obj.appliedAmount = 0;
                obj.currentAppliedAmount = 0;
                obj.unappliedAmount = Math.abs(lineItem.openBalance);
                obj.remainingUnapplied = this.precisionService.getRoundedNumber(
                    obj.unappliedAmount - obj.appliedAmount
                );

                sourceLineItems.push(obj);
            });
        }

        this.creditApplicationSourceLineItems = sourceLineItems;
    }

    private calculateRemainingAppliedAmount(): void {
        const appliedAmountList = Utils.notNullOrEmpty(this.editorResult.targets)
            ? this.editorResult.targets.map((d) => d.appliedAmount)
            : [];
        this.totalAppliedAmount = Utils.notNullOrEmpty(appliedAmountList)
            ? this.precisionService.getRoundedNumber(appliedAmountList.reduce((p, n) => p + n))
            : 0;
        const remainingUnappliedAmount = this.editorResult.source.unappliedAmount - this.totalAppliedAmount;
        this.remainingUnappliedAmount = this.precisionService.getRoundedNumber(remainingUnappliedAmount);
        this.calculateMinApplyDate();
    }

    private calculateMinApplyDate(): void {
        let postDates = [];
        const selectedRows = this.editorResult.targets.filter((d) => Utils.isTrue(d.isSelect));
        if (Utils.notNullOrEmpty(selectedRows)) {
            postDates = selectedRows.map((d) => d.postDate);
            postDates.push(this.editorResult.closedBookPeriod);
        } else {
            postDates.push(this.editorResult.source.postDate, this.editorResult.closedBookPeriod);
            this.editorResult.targets.forEach((d) => {
                postDates.push(d.postDate);
            });
        }

        this.minApplyDate = new Date(Math.max.apply(null, postDates));
    }

    private prepareCreditApplicationTargetItems(target: ARCreditApplicationTargetTransactionResultDto): void {
        if (Utils.isTrue(target.isSelect) && target.lineItems.length === 1 && target.appliedAmount > 0) {
            const targetLineItem = target.lineItems[0];
            const transactionItem: ARCreditApplicationTransactionItemParametersDto =
                new ARCreditApplicationTransactionItemParametersDto();

            const foundAtIndex = this.creditApplicationTargetItems.findIndex(
                (d) =>
                    Utils.equals(targetLineItem.sourceItemId, d.sourceItemId) && Utils.equals(target.id, d['targetId'])
            );

            transactionItem.sourceItemId = targetLineItem.sourceItemId;
            transactionItem.targetItemId = targetLineItem.itemId;
            transactionItem.applyAmount = targetLineItem.appliedAmount;
            transactionItem['targetId'] = target.id;

            if (foundAtIndex > -1) {
                this.creditApplicationTargetItems[foundAtIndex] = transactionItem;
            } else {
                this.creditApplicationTargetItems.push(transactionItem);
            }
        }
        this.calculateRemainingAppliedAmount();
    }

    private closeCreditApplication(): void {
        this.visible = false;
        this.onManualCAEditorClose.emit(false);
    }
}
